/**
 * A company's billing terms: how its lines are exported, what each case type
 * costs and who pays, the rates its e-billing approved, the IDs its AP needs,
 * the outside counsel guidelines pre-bill checks against, its budget, and a
 * preview of the next export. CaseBlink exports the file;
 * the firm's accounting team or billing system sends and collects invoices.
 */
import {
  AlertTriangle,
  Check,
  CircleCheck,
  Info,
  Lock,
  Plus,
  Trash2
} from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Surface } from "@/components/surface";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { EMPLOYMENT_BASED_VISAS, VISAVALUE } from "@/types/visa.type";

import { useTimeStatus } from "../../timeTracking/queries";
import { GuidelinesSection } from "../../timeTracking/rules/GuidelinesSection";
import { Segmented } from "../../timeTracking/settings/SettingRow";
import { formatHours, formatMoney } from "../../timeTracking/utils";
import { billingActions } from "../actions";
import {
  BASIS_OPTIONS,
  feeText,
  FORMAT_OPTIONS,
  GROUPING_OPTIONS,
  groupingConflict,
  LEGAL_TRACKER,
  lockLabel,
  lockReason,
  PO_FIELD_OPTIONS,
  rateEffect,
  ratesWith,
  SHARE_OPTIONS,
  shareText,
  withFormat,
  withPoField,
  type Tone
} from "../billingModel";
import { useCompanyBilling, useNextExport } from "../queries";
import { companyPath } from "../routes";
import type {
  BillingPatch,
  BudgetLine,
  Company,
  CompanyBilling,
  ExportSetup,
  FeeBasis,
  FeeScheduleRow,
  NextExportInvoice
} from "../types";
import { formatDate, initialsOf } from "../utils";

const NONE = "__none";
const EMPLOYMENT = Object.values(VISAVALUE).filter((v) =>
  EMPLOYMENT_BASED_VISAS.has(v)
);

const TONE: Record<Tone, string> = {
  primary: "border-primary/30 bg-primary/10 text-primary",
  emerald: "border-success-line bg-success-soft text-success",
  amber: "border-warn-line bg-warn-soft text-warn",
  red: "border-destructive/30 bg-destructive/10 text-danger"
};

const Tag: FC<{ tone?: Tone; children: ReactNode }> = ({ tone, children }) => (
  <Badge
    variant="secondary"
    className={cn("font-normal whitespace-nowrap", tone && TONE[tone])}
  >
    {children}
  </Badge>
);

const Section: FC<{
  title: string;
  desc?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  testId?: string;
}> = ({ title, desc, aside, children, testId }) => (
  <div className="flex flex-col gap-2.5" data-testid={testId}>
    <div className="flex items-end gap-3">
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium">{title}</span>
        {desc ? (
          <span className="max-w-190 text-sm leading-4.5 text-fg-3">
            {desc}
          </span>
        ) : null}
      </span>
      {aside}
    </div>
    {children}
  </div>
);

const Card: FC<{ children: ReactNode }> = ({ children }) => (
  <Surface className="gap-3 p-3.5">{children}</Surface>
);

const KV: FC<{ k: string; mono?: boolean; children: ReactNode }> = ({
  k,
  mono,
  children
}) => (
  <div className="grid grid-cols-[170px_minmax(0,1fr)] items-center gap-3 text-base">
    <span className={cn("text-fg-3", mono && "font-mono text-sm")}>{k}</span>
    <span className="flex min-w-0 flex-wrap items-center gap-2">
      {children}
    </span>
  </div>
);

const Note: FC<{ warn?: boolean; children: ReactNode }> = ({
  warn,
  children
}) => (
  <span
    className={cn(
      "flex items-start gap-2 text-sm leading-4.5",
      warn ? "text-warn" : "text-fg-3"
    )}
  >
    {warn ? (
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
    ) : (
      <Info className="mt-0.5 size-3.5 shrink-0" />
    )}
    <span>{children}</span>
  </span>
);

/** An input that saves when it loses focus or on Enter, only if it changed. */
const CommitInput: FC<{
  id?: string;
  label: string;
  value: string;
  onCommit: (value: string) => void;
  placeholder?: string;
  className?: string;
  type?: string;
  disabled?: boolean;
}> = ({
  id,
  label,
  value,
  onCommit,
  placeholder,
  className,
  type,
  disabled
}) => {
  const [draft, setDraft] = useState(value);
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(value);
  }
  const commit = () => {
    if (draft.trim() !== value) onCommit(draft.trim());
  };
  return (
    <Input
      id={id}
      aria-label={label}
      type={type}
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      className={cn("h-7 text-base", className)}
      onChange={(e) => setDraft(e.currentTarget.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
    />
  );
};

const toAmount = (v: string): number | null => {
  const n = Number(v.replace(/[^0-9.]/g, ""));
  return n > 0 ? n : null;
};

type SectionProps = {
  company: Company;
  billing: CompanyBilling;
  canEdit: boolean;
  save: (patch: BillingPatch, message?: string) => void;
};

const ExportSetupSection: FC<SectionProps> = ({
  company,
  billing,
  canEdit,
  save
}) => {
  const setup = billing.export;
  const put = (next: ExportSetup, message: string) =>
    save({ export: next }, message);
  return (
    <Section
      title="Export setup"
      testId="billing-export-setup"
      desc={`How ${company.name}'s lines are grouped and formatted in the export. Your accounting team or billing system numbers, sends and collects the invoices, the same as for individual clients.`}
    >
      <Card>
        <KV k="Group into">
          <Segmented
            label="Group into"
            value={setup.grouping}
            options={GROUPING_OPTIONS}
            disabled={!canEdit}
            onChange={(grouping) =>
              put({ ...setup, grouping }, "Changed the grouping")
            }
          />
        </KV>
        <KV k="Format">
          <Segmented
            label="Format"
            value={setup.format}
            options={FORMAT_OPTIONS}
            disabled={!canEdit}
            onChange={(format) =>
              put(withFormat(setup, format), "Changed the export format")
            }
          />
        </KV>
        <KV k="E-billing system">
          <Select
            value={setup.ebilling_system || NONE}
            disabled={!canEdit}
            onValueChange={(v) =>
              put(
                { ...setup, ebilling_system: v === NONE ? "" : v },
                "Saved the e-billing system"
              )
            }
          >
            <SelectTrigger
              size="sm"
              className="w-55"
              aria-label="E-billing system"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>None</SelectItem>
              {billing.ebilling_systems.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-fg-3">
            Recorded so the export meets its rules. Your accounting team uploads
            the file.
          </span>
        </KV>
        {groupingConflict(setup) ? (
          <Note warn>
            {LEGAL_TRACKER} takes one matter per invoice, so an export grouped
            by entity won&apos;t upload.
          </Note>
        ) : (
          <Note>
            {setup.grouping === "matter"
              ? "Each employee matter becomes its own invoice in the file, so AP can match it to its PO."
              : "Each entity becomes one invoice in the file, with lines for every employee and their cost center."}
          </Note>
        )}
      </Card>
    </Section>
  );
};

const AddFeeRow: FC<{
  billing: CompanyBilling;
  onAdd: (row: Partial<FeeScheduleRow>) => void;
}> = ({ billing, onAdd }) => {
  const taken = new Set(billing.fee_schedule.map((r) => r.case_type));
  const own = billing.case_types.filter((t) => !taken.has(t));
  const others = [
    ...new Set<string>([billing.response_case_type, ...EMPLOYMENT])
  ]
    .filter((t) => !taken.has(t) && !own.includes(t))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const [caseType, setCaseType] = useState("");
  const [basis, setBasis] = useState<FeeBasis>("flat");
  const [amount, setAmount] = useState("");
  const [billOn, setBillOn] = useState("");
  const needsAmount = basis !== "hourly";
  const ready = !!caseType && (!needsAmount || !!toAmount(amount));
  const add = () => {
    onAdd({
      case_type: caseType,
      label: caseType,
      basis,
      amount: needsAmount ? toAmount(amount) : null,
      bill_on: billOn,
      employer_share: 100
    });
    setCaseType("");
    setAmount("");
    setBillOn("");
  };
  return (
    <div
      className="flex flex-wrap items-end gap-2 border-t border-line-2 px-3.5 py-2.5"
      data-testid="fee-add-row"
    >
      <span className="flex flex-col gap-1">
        <Label htmlFor="fee-add-type" className="text-sm">
          Case type
        </Label>
        <Select value={caseType} onValueChange={setCaseType}>
          <SelectTrigger id="fee-add-type" size="sm" className="w-42.5">
            <SelectValue placeholder="Pick a case type" />
          </SelectTrigger>
          <SelectContent>
            {own.length ? (
              <SelectGroup>
                <SelectLabel>On this company&apos;s cases</SelectLabel>
                {own.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectGroup>
            ) : null}
            {own.length && others.length ? <SelectSeparator /> : null}
            <SelectGroup>
              {own.length ? <SelectLabel>Other case types</SelectLabel> : null}
              {others.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </span>
      <span className="flex flex-col gap-1">
        <Label htmlFor="fee-add-basis" className="text-sm">
          Basis
        </Label>
        <Select value={basis} onValueChange={(v) => setBasis(v as FeeBasis)}>
          <SelectTrigger id="fee-add-basis" size="sm" className="w-45">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BASIS_OPTIONS.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </span>
      {needsAmount ? (
        <span className="flex flex-col gap-1">
          <Label htmlFor="fee-add-amount" className="text-sm">
            {basis === "flat" ? "Fee" : "Not to exceed"}
          </Label>
          <Input
            id="fee-add-amount"
            inputMode="decimal"
            placeholder="$0"
            className="h-8 w-27.5 text-base"
            value={amount}
            onChange={(e) => setAmount(e.currentTarget.value)}
          />
        </span>
      ) : null}
      <span className="flex flex-col gap-1">
        <Label htmlFor="fee-add-bill-on" className="text-sm">
          Billed
        </Label>
        <Input
          id="fee-add-bill-on"
          placeholder="On filing"
          className="h-8 w-40 text-base"
          value={billOn}
          onChange={(e) => setBillOn(e.currentTarget.value)}
        />
      </span>
      <Button size="sm" variant="secondary" disabled={!ready} onClick={add}>
        <Plus /> Add case type
      </Button>
    </div>
  );
};

const FEE_COLS =
  "grid grid-cols-[minmax(0,1.3fr)_150px_minmax(0,1fr)_230px_minmax(0,1fr)_32px] items-center gap-3 px-3.5";

const FeeScheduleSection: FC<SectionProps> = ({
  company,
  billing,
  canEdit,
  save
}) => {
  const rows = billing.fee_schedule;
  const put = (next: Array<Partial<FeeScheduleRow>>, message: string) =>
    save({ fee_schedule: next }, message);
  const patchRow = (
    row: FeeScheduleRow,
    patch: Partial<FeeScheduleRow>,
    message: string
  ) =>
    put(
      rows.map((r) => (r.id === row.id ? { ...r, ...patch } : r)),
      message
    );
  return (
    <Section
      title="Fee schedule and who pays"
      testId="billing-fee-schedule"
      desc={`${company.name}'s agreed price for each case type and how each fee is split between ${company.name} and the employee. Linked cases pick this up unless a case sets its own fee. Rows locked by law can't be changed for any company.`}
    >
      <Surface className="shrink-0">
        <div
          className={cn(
            FEE_COLS,
            "h-8 border-b border-line-2 bg-fill-1 text-sm text-fg-3"
          )}
        >
          <span>Case type</span>
          <span>Fee</span>
          <span>Billed</span>
          <span>{company.name} pays</span>
          <span>Employee pays</span>
          <span />
        </div>
        {rows.length ? null : (
          <div className="px-3.5 py-4 text-base text-fg-3">
            No fee schedule yet. Cases use the fee set on each case.
          </div>
        )}
        {rows.map((row) => {
          const lock = lockLabel(row, billing);
          return (
            <div
              key={row.id}
              className={cn(
                FEE_COLS,
                "min-h-12 border-b border-line-2 py-2 last:border-b-0"
              )}
              data-testid={`fee-row-${row.case_type}`}
            >
              <span className="truncate text-base font-medium">
                {row.label}
              </span>
              {canEdit && row.basis !== "hourly" ? (
                <span className="flex items-center gap-1.5">
                  <span className="text-base text-fg-3">$</span>
                  <CommitInput
                    label={`${row.label} fee`}
                    value={String(row.amount ?? "")}
                    className="w-21 tabular-nums"
                    onCommit={(v) => {
                      const amount = toAmount(v);
                      if (amount)
                        patchRow(row, { amount }, `Saved the ${row.label} fee`);
                    }}
                  />
                  <span className="text-sm text-fg-3">
                    {row.basis === "flat" ? "flat" : "NTE"}
                  </span>
                </span>
              ) : (
                <span className="text-base tabular-nums">{feeText(row)}</span>
              )}
              {canEdit ? (
                <CommitInput
                  label={`When ${row.label} is billed`}
                  value={row.bill_on}
                  placeholder="On filing"
                  onCommit={(v) =>
                    patchRow(
                      row,
                      { bill_on: v },
                      `Saved when ${row.label} is billed`
                    )
                  }
                />
              ) : (
                <span className="text-sm text-fg-3">{row.bill_on || "–"}</span>
              )}
              {lock ? (
                <button
                  type="button"
                  title={lockReason(row, billing)}
                  onClick={() => toast(lockReason(row, billing))}
                  className="-mx-1 inline-flex w-fit items-center gap-1.5 rounded-md px-1 py-0.5 text-base hover:bg-fill-2"
                  data-testid={`fee-lock-${row.case_type}`}
                >
                  <Lock className="size-3.5 text-fg-3" />
                  100%
                  <span className="text-sm text-fg-3">{lock}</span>
                </button>
              ) : (
                <Segmented
                  label={`${company.name}'s share of ${row.label}`}
                  value={String(row.employer_share) as "100" | "50" | "0"}
                  options={SHARE_OPTIONS}
                  disabled={!canEdit}
                  onChange={(v) =>
                    patchRow(
                      row,
                      { employer_share: Number(v) },
                      `${company.name} pays ${v}% of ${row.label}`
                    )
                  }
                />
              )}
              <span
                className={cn(
                  "text-base tabular-nums",
                  row.employer_share < 100 ? "text-primary" : "text-fg-3"
                )}
              >
                {shareText(row.employer_share)}
              </span>
              {canEdit ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Remove ${row.label}`}
                  onClick={() =>
                    put(
                      rows.filter((r) => r.id !== row.id),
                      `Removed ${row.label} from the fee schedule`
                    )
                  }
                >
                  <Trash2 />
                </Button>
              ) : (
                <span />
              )}
            </div>
          );
        })}
        {canEdit ? (
          <AddFeeRow
            billing={billing}
            onAdd={(row) =>
              put([...rows, row], `Added ${row.label} to the fee schedule`)
            }
          />
        ) : null}
      </Surface>
      <Note>
        USCIS filing fees, premium processing and recruitment ads pass through
        at cost as expense lines and follow the same split. The employee can pay
        premium processing only when it&apos;s purely for their own benefit,
        such as urgent travel.
      </Note>
    </Section>
  );
};

const RATE_COLS =
  "grid grid-cols-[minmax(0,1.2fr)_90px_160px_minmax(0,1fr)_170px] items-center gap-3 px-3.5";

const RatesSection: FC<SectionProps> = ({
  company,
  billing,
  canEdit,
  save
}) => {
  const system = billing.export.ebilling_system || "their e-billing system";
  const sheet = [
    billing.rate_sheet_approved_on &&
      `Rate sheet approved ${formatDate(billing.rate_sheet_approved_on)}`,
    billing.rate_sheet_next_due &&
      `next rates due ${formatDate(billing.rate_sheet_next_due)}`
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Section
      title="Approved rates"
      testId="billing-rates"
      desc={`${company.name} approves rates in ${system}. Record the approved rates here so exports price lines correctly. Lines from a timekeeper with no approved rate block the export, and approved rates below standard are applied in pre-bill.`}
      aside={sheet ? <span className="text-sm text-fg-3">{sheet}</span> : null}
    >
      <Surface className="shrink-0">
        <div
          className={cn(
            RATE_COLS,
            "h-8 border-b border-line-2 bg-fill-1 text-sm text-fg-3"
          )}
        >
          <span>Timekeeper</span>
          <span className="text-right">Standard</span>
          <span className="text-right">{company.name} approved</span>
          <span>Effect</span>
          <span />
        </div>
        {billing.timekeepers.map((t) => {
          const rate = billing.approved_rates.find(
            (r) => r.user_id === t.user_id
          );
          const effect = rateEffect(rate, t.standard_rate, formatDate);
          const approved = rate?.status === "approved" ? rate.rate : null;
          return (
            <div
              key={t.user_id}
              className={cn(
                RATE_COLS,
                "min-h-12 border-b border-line-2 py-2 last:border-b-0"
              )}
              data-testid={`rate-row-${t.user_id}`}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="flex size-5.5 shrink-0 items-center justify-center rounded-md bg-fill-2 text-sm font-medium">
                  {initialsOf(t.name)}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-medium">
                    {t.name}
                  </span>
                  <span className="truncate text-sm text-fg-3">
                    {t.classification ?? t.role ?? ""}
                  </span>
                </span>
              </span>
              <span className="text-right text-base text-fg-3 tabular-nums">
                {t.standard_rate ? `${formatMoney(t.standard_rate)}/h` : "–"}
              </span>
              <span className="flex justify-end">
                {canEdit ? (
                  <CommitInput
                    label={`${company.name} approved rate for ${t.name}`}
                    value={approved ? String(approved) : ""}
                    placeholder="–"
                    className="w-25 text-right tabular-nums"
                    onCommit={(v) =>
                      save(
                        {
                          approved_rates: ratesWith(
                            billing.approved_rates,
                            t.user_id,
                            toAmount(v)
                          )
                        },
                        toAmount(v)
                          ? `Recorded ${t.name}'s approved rate`
                          : `Cleared ${t.name}'s approved rate`
                      )
                    }
                  />
                ) : (
                  <span className="text-base font-medium tabular-nums">
                    {approved ? `${formatMoney(approved)}/h` : "–"}
                  </span>
                )}
              </span>
              <span>
                <Tag tone={effect.tone}>{effect.label}</Tag>
              </span>
              <span className="flex justify-end">
                {canEdit && !approved && rate?.status !== "pending" ? (
                  <Button
                    size="xs"
                    variant="secondary"
                    onClick={() =>
                      void billingActions.markSubmitted(company, t.user_id)
                    }
                  >
                    <Check /> Mark as submitted
                  </Button>
                ) : null}
              </span>
            </div>
          );
        })}
      </Surface>
      {canEdit ? (
        <div className="flex flex-wrap items-center gap-3 text-sm text-fg-3">
          <Label htmlFor="rate-sheet-approved" className="text-sm font-normal">
            Rate sheet approved
          </Label>
          <CommitInput
            id="rate-sheet-approved"
            label="Rate sheet approved"
            type="date"
            className="w-37.5"
            value={billing.rate_sheet_approved_on ?? ""}
            onCommit={(v) =>
              save(
                { rate_sheet_approved_on: v || null },
                "Saved the rate sheet date"
              )
            }
          />
          <Label htmlFor="rate-sheet-next" className="text-sm font-normal">
            Next rates due
          </Label>
          <CommitInput
            id="rate-sheet-next"
            label="Next rates due"
            type="date"
            className="w-37.5"
            value={billing.rate_sheet_next_due ?? ""}
            onCommit={(v) =>
              save(
                { rate_sheet_next_due: v || null },
                "Saved when the next rates are due"
              )
            }
          />
        </div>
      ) : null}
    </Section>
  );
};

const IdsSection: FC<SectionProps & { showExport: boolean }> = ({
  company,
  billing,
  canEdit,
  save,
  showExport
}) => {
  const setup = billing.export;
  const alloc = billing.allocations;
  const centers = alloc.cost_centers.length
    ? alloc.cost_centers
    : billing.budget.lines.map((l) => l.cost_center);
  const putExport = (next: ExportSetup, message: string) =>
    save({ export: next }, message);
  return (
    <Section
      title="E-billing IDs and allocations"
      testId="billing-ids"
      desc={`Everything ${company.name}'s AP and e-billing system need to accept and book an invoice. CaseBlink checks these before export.`}
    >
      <Card>
        <KV k="CLIENT_ID" mono>
          {company.client_no ? (
            <span className="font-mono text-sm">{company.client_no}</span>
          ) : (
            <span className="text-warn">Add a client no. in Edit company</span>
          )}
        </KV>
        {showExport ? (
          <KV k="LAW_FIRM_ID" mono>
            {billing.firm_ledes_id ? (
              <span className="font-mono text-sm">{billing.firm_ledes_id}</span>
            ) : (
              <span className="text-warn">
                Add your firm&apos;s LEDES ID in Time &amp; billing settings
              </span>
            )}
          </KV>
        ) : null}
        <KV k={`${company.name} matter ID`}>
          <Switch
            aria-label={`Require ${company.name}'s matter ID`}
            checked={setup.require_client_matter_id}
            disabled={!canEdit}
            onCheckedChange={(on) =>
              putExport(
                { ...setup, require_client_matter_id: on },
                on
                  ? "Client matter IDs are required"
                  : "Client matter IDs are optional"
              )
            }
          />
          <span>
            Required on every employee matter, sent as CLIENT_MATTER_ID
          </span>
          {canEdit ? (
            <CommitInput
              label="Client matter ID format"
              value={setup.client_matter_id_format}
              placeholder="IMM-YY-####"
              className="w-32.5 font-mono text-sm"
              onCommit={(v) =>
                putExport(
                  { ...setup, client_matter_id_format: v },
                  "Saved the matter ID format"
                )
              }
            />
          ) : setup.client_matter_id_format ? (
            <span className="font-mono text-sm text-fg-3">
              {setup.client_matter_id_format}
            </span>
          ) : null}
          {setup.require_client_matter_id &&
          billing.missing_client_matter_ids ? (
            <Link
              to={companyPath(company.id, "employees")}
              className="text-sm text-warn hover:underline"
            >
              {billing.missing_client_matter_ids} missing
            </Link>
          ) : null}
        </KV>
        <KV k="PO number">
          <Switch
            aria-label="Require a PO"
            checked={setup.require_po}
            disabled={!canEdit}
            onCheckedChange={(on) =>
              putExport(
                { ...setup, require_po: on },
                on ? "POs are required" : "POs are optional"
              )
            }
          />
          <span>Required, one PO per employee matter</span>
        </KV>
        {setup.require_po && showExport ? (
          <KV k="PO goes in">
            <Segmented
              label="PO goes in"
              value={setup.po_field}
              options={PO_FIELD_OPTIONS}
              disabled={!canEdit}
              onChange={(v) =>
                putExport(withPoField(setup, v), "Saved where the PO goes")
              }
            />
          </KV>
        ) : null}
        <KV k="Cost center">
          <Segmented
            label="Cost center comes from"
            value={alloc.cost_center_source}
            options={[
              { id: "department", label: "Employee's department" },
              { id: "case", label: "Set on each case" }
            ]}
            disabled={!canEdit}
            onChange={(v) =>
              save(
                { allocations: { ...alloc, cost_center_source: v } },
                "Saved where cost centers come from"
              )
            }
          />
          {centers.map((c) => (
            <Tag key={c}>{c}</Tag>
          ))}
        </KV>
        <KV k="GL account">
          {canEdit ? (
            <CommitInput
              label="GL account"
              value={alloc.gl_account}
              placeholder="6420 · Legal, immigration"
              className="w-60 font-mono text-sm"
              onCommit={(v) =>
                save(
                  { allocations: { ...alloc, gl_account: v } },
                  "Saved the GL account"
                )
              }
            />
          ) : (
            <span className="font-mono text-sm">{alloc.gl_account || "–"}</span>
          )}
        </KV>
      </Card>
    </Section>
  );
};

const BudgetSection: FC<SectionProps> = ({
  company,
  billing,
  canEdit,
  save
}) => {
  const { budget, spend } = billing;
  const total =
    budget.total ?? budget.lines.reduce((s, l) => s + (l.amount || 0), 0);
  const [adding, setAdding] = useState<BudgetLine>({
    cost_center: "",
    name: "",
    amount: 0
  });
  const putLines = (lines: BudgetLine[], message: string) =>
    save({ budget: { ...budget, lines } }, message);
  return (
    <Section
      title="Budget"
      testId="billing-budget"
      desc={`${company.name}'s ${
        budget.fiscal_year ? `FY${budget.fiscal_year} ` : ""
      }immigration budget by cost center. Billed is the company's share of exported lines; unbilled is released time, fees and costs not exported yet.`}
      aside={
        <span className="text-sm text-fg-3 tabular-nums">
          {formatMoney(spend.billed)} billed · {formatMoney(spend.unbilled)}{" "}
          unbilled{total ? ` · ${formatMoney(total)} budget` : ""}
        </span>
      }
    >
      <Card>
        {canEdit ? (
          <KV k="Fiscal year">
            <CommitInput
              label="Fiscal year"
              value={budget.fiscal_year}
              placeholder="2026"
              className="w-22.5"
              onCommit={(v) =>
                save(
                  { budget: { ...budget, fiscal_year: v } },
                  "Saved the fiscal year"
                )
              }
            />
          </KV>
        ) : null}
        {budget.lines.length ? null : (
          <span className="text-base text-fg-3">No budget recorded.</span>
        )}
        {budget.lines.map((line) => {
          const spent = spend.by_cost_center[line.cost_center] ?? 0;
          const pct = line.amount
            ? Math.min(100, (spent / line.amount) * 100)
            : 0;
          return (
            <div
              key={line.cost_center}
              className="grid grid-cols-[180px_minmax(0,1fr)_170px_28px] items-center gap-3.5"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-base font-medium">
                  {line.name || line.cost_center}
                </span>
                <span className="text-sm text-fg-3">{line.cost_center}</span>
              </span>
              <Progress
                value={pct}
                aria-label={`${line.cost_center} budget used`}
                className={cn(pct > 70 && "[&>*]:bg-warn")}
              />
              <span className="text-right text-sm tabular-nums">
                {formatMoney(spent)}{" "}
                <span className="text-fg-3">of {formatMoney(line.amount)}</span>
              </span>
              {canEdit ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Remove ${line.cost_center}`}
                  onClick={() =>
                    putLines(
                      budget.lines.filter(
                        (l) => l.cost_center !== line.cost_center
                      ),
                      `Removed ${line.cost_center} from the budget`
                    )
                  }
                >
                  <Trash2 />
                </Button>
              ) : (
                <span />
              )}
            </div>
          );
        })}
        {canEdit ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-line-2 pt-3">
            <Input
              aria-label="Cost center"
              placeholder="Cost center"
              className="h-7 w-30 font-mono text-sm"
              value={adding.cost_center}
              onChange={(e) =>
                setAdding({ ...adding, cost_center: e.currentTarget.value })
              }
            />
            <Input
              aria-label="Cost center name"
              placeholder="Name"
              className="h-7 w-45 text-base"
              value={adding.name}
              onChange={(e) =>
                setAdding({ ...adding, name: e.currentTarget.value })
              }
            />
            <Input
              aria-label="Budget amount"
              placeholder="$0"
              inputMode="decimal"
              className="h-7 w-27.5 text-base"
              value={adding.amount ? String(adding.amount) : ""}
              onChange={(e) =>
                setAdding({
                  ...adding,
                  amount: toAmount(e.currentTarget.value) ?? 0
                })
              }
            />
            <Button
              size="sm"
              variant="secondary"
              disabled={!adding.cost_center.trim()}
              onClick={() => {
                putLines(
                  [
                    ...budget.lines.filter(
                      (l) => l.cost_center !== adding.cost_center.trim()
                    ),
                    { ...adding, cost_center: adding.cost_center.trim() }
                  ],
                  `Saved the ${adding.cost_center.trim()} budget`
                );
                setAdding({ cost_center: "", name: "", amount: 0 });
              }}
            >
              <Plus /> Add cost center
            </Button>
          </div>
        ) : null}
      </Card>
    </Section>
  );
};

const InvoiceCard: FC<{
  invoice: NextExportInvoice;
  poInDescription: boolean;
}> = ({ invoice, poInDescription }) => {
  const employee = invoice.payer === "employee";
  return (
    <Surface
      className={cn(invoice.issues.length && "ring-warn")}
      data-testid={`next-invoice-${invoice.number}`}
    >
      <div className="flex items-center gap-2.5 border-b border-line-2 bg-fill-1 px-3.5 py-2.5">
        <span className="font-mono text-sm font-medium">{invoice.number}</span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-base font-medium">
            {invoice.client_name || invoice.label}
          </span>
          <span className="truncate text-sm text-fg-3">
            {invoice.label}
            {invoice.client_no ? ` · Client no. ${invoice.client_no}` : ""}
          </span>
        </span>
        {employee ? <Tag tone="primary">Employee Share</Tag> : null}
        <span className="flex-1" />
        <span className="text-sm font-medium tabular-nums">
          {formatMoney(invoice.total)}
        </span>
        {invoice.issues.length ? (
          <Tag tone="amber">{invoice.issues.length} to fix</Tag>
        ) : (
          <Tag tone="emerald">
            <CircleCheck className="size-3" /> Ready
          </Tag>
        )}
      </div>
      {invoice.lines.map((line) => (
        <div
          key={`${invoice.number}-${line.individual_id}`}
          className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_60px_90px] items-center gap-3 border-b border-line-2 px-3.5 py-2 last:border-b-0"
        >
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-base">{line.case_label}</span>
            <span className="truncate text-sm text-fg-3">
              {line.basis} · {formatHours(line.hours)}
            </span>
          </span>
          <span className="font-mono text-sm leading-4.25 text-fg-3">
            {line.matter_no || "<no matter no.>"} ·{" "}
            {line.client_matter_id || "<no client matter ID>"}
            <br />
            {employee
              ? "Employee Share"
              : [
                  line.po &&
                    `PO ${line.po}${
                      poInDescription ? " (in description)" : ""
                    }`,
                  line.cost_center
                ]
                  .filter(Boolean)
                  .join(" · ") || "–"}
          </span>
          <span className="text-right text-sm text-fg-3 tabular-nums">
            {line.share}%
          </span>
          <span className="text-right text-base tabular-nums">
            {formatMoney(line.amount)}
          </span>
        </div>
      ))}
      {invoice.issues.length ? (
        <div className="flex flex-col gap-1 bg-warn-soft px-3.5 py-2">
          {invoice.issues.map((issue) => (
            <Note key={`${issue.kind}-${issue.id}`} warn>
              {issue.message}
            </Note>
          ))}
        </div>
      ) : null}
    </Surface>
  );
};

const NextExportSection: FC<{ company: Company; billing: CompanyBilling }> = ({
  company,
  billing
}) => {
  const next = useNextExport(company.id);
  const { data } = next;
  const invoices = data?.invoices ?? [];
  const onInvoice = new Set(
    invoices.flatMap((inv) => inv.issues.map((i) => `${i.kind}-${i.id}`))
  );
  return (
    <Section
      title="Next export"
      testId="billing-next-export"
      desc={`What the next export file contains for ${company.name} with these settings, from approved time not yet exported. Your accounting team takes it from there. The employee's share is exported under the employee as the client and never appears on ${company.name}'s invoices.`}
      aside={
        data?.count ? (
          <span className="text-sm text-fg-3">
            {data.format_label} · {invoices.length}{" "}
            {invoices.length === 1 ? "invoice" : "invoices"} in the file
          </span>
        ) : null
      }
    >
      {next.isLoading ? <Skeleton className="h-28" /> : null}
      {data && !invoices.length ? (
        <span className="rounded-xl bg-fill-1 px-3.5 py-4 text-base text-fg-3">
          Nothing approved and unexported for {company.name} yet.
        </span>
      ) : null}
      {(data?.issues ?? [])
        .filter((i) => !onInvoice.has(`${i.kind}-${i.id}`))
        .map((issue) => (
          <Note key={`${issue.kind}-${issue.id}`} warn>
            {issue.message}
          </Note>
        ))}
      {invoices.map((inv) => (
        <InvoiceCard
          key={inv.number}
          invoice={inv}
          poInDescription={billing.export.po_field === "description"}
        />
      ))}
    </Section>
  );
};

export const BillingTab: FC<{ company: Company }> = ({ company }) => {
  const query = useCompanyBilling(company.id);
  const status = useTimeStatus();
  const billing = query.data;
  if (query.isLoading || status.isLoading)
    return <Skeleton className="m-4 h-60" />;
  if (!billing)
    return (
      <div className="p-4 text-base text-fg-3">
        Couldn&apos;t load {company.name}&apos;s billing terms.
      </div>
    );
  const canEdit = status.is_admin;
  const save = (patch: BillingPatch, message?: string) =>
    void billingActions.save(company, billing, patch, message);
  const props: SectionProps = { company, billing, canEdit, save };
  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto px-5 pt-4.5 pb-7"
      data-testid="company-billing"
    >
      <div className="flex max-w-265 flex-col gap-7">
        {canEdit ? null : (
          <Note>Only firm admins can change billing terms.</Note>
        )}
        {status.enabled ? <ExportSetupSection {...props} /> : null}
        <FeeScheduleSection {...props} />
        {status.enabled ? <RatesSection {...props} /> : null}
        <IdsSection {...props} showExport={status.enabled} />
        {status.enabled && canEdit ? (
          <GuidelinesSection
            owner={{ company_id: company.id }}
            ownerName={company.name}
            canEdit
          />
        ) : null}
        <BudgetSection {...props} />
        {status.enabled && canEdit ? (
          <NextExportSection company={company} billing={billing} />
        ) : null}
      </div>
    </div>
  );
};
